import React from "react";

import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

type DropdownArguments = {
  label: string;
  options: string[];
};

interface Props {
  element: FormElement<DropdownArguments>;
}

const FormDropdown: React.FC<Props> = observer(({ element }) => {
  const editing = useEditing(element);
  const { classes } = useStyles();

  function handleEditOption(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) {
    element.updateArgument("options", (options) => {
      if (options) {
        options[index] = event.target.value;
      }
      return options;
    });
  }

  function handleRemoveOption(index: number) {
    element.updateArgument("options", (options) => {
      if (options) {
        options.splice(index, 1);
      }
      return options;
    });
  }

  const handleAddOption = () => {
    element.updateArgument("options", (options) => {
      if (options) {
        options.push("");
        return options;
      }
      return [""];
    });
  };

  return (
    <>
      {!editing.value ? (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon
            className={clsx(["styled-drag-indicator"])}
          />
          <div className={classes.longTextInputContent}>
            <Typography
              variant="h6"
              color="textPrimary"
              style={{ marginBottom: 12 }}
            >
              {element.arguments.label || "Drop down label"}
            </Typography>
            <Select
              variant="outlined"
              style={{ width: "100%", marginBottom: 12 }}
              id="simple-select"
              value={element.arguments.options?.[0]}
            >
              {element.arguments.options?.map((e: string, i: number) => (
                <MenuItem value={e} key={i}>
                  {e}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className={clsx([classes.editIcons, "edit-icons-container"])}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon onClick={() => element.remove()} />
          </div>
        </Box>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Drop down label
              </Typography>
              <TextField
                value={element.arguments.label}
                onChange={(e) => element.setArgument("label", e.target.value)}
              />
              <Typography variant="body2" color="textSecondary">
                Options
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                marginBottom={4}
              >
                {element.arguments.options?.map((e: string, i: number) => (
                  <TextField
                    placeholder="Enter short input label"
                    style={{ margin: 8 }}
                    onChange={(e) => handleEditOption(e, i)}
                    value={e}
                    key={i}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleRemoveOption(i)}
                          size="large"
                        >
                          <CloseIcon />
                        </IconButton>
                      ),
                    }}
                  />
                ))}
              </Box>
              <div style={{ width: "30%" }}>
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                >
                  Add item
                </Button>
              </div>
              <StyledHorizontalDivider orientation="horizontal" />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        aria-label="Required"
                        checked={element.required}
                        onChange={(e) => element.setRequired(e.target.checked)}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Required"
                  />
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormDropdown;
